// BL-747 — the single source of truth for "can this auction be set to Ready?"
//
// BL-1286 moved this from `apps/admin-web/src/lib/auctions/` into core-admin
// unchanged. It was already an IsomorphicService by nature (rule #21 / ADR 010:
// pure, read-only, identical on both sides) and was already consumed by BOTH a
// client component and a server service — it simply lived in the app, so no
// shared package could reference `AuctionReadyBlocker`. The BL-1286
// transition-check response DTO needs exactly that type, and matrix §4.2
// generalizes this gate from the single Draft → Ready transition to EVERY
// transition that publishes.
// Ready = visible on the bidder website (the is_listed toggle is retired), so
// these are the go-live completeness gates.
//
// FIVE server-side doors read this one function, which is what stops any of them
// becoming a way around the front door: `mark-ready`, `open` and
// `open-pre-bidding` via `lockAndGate`; `restore` → ready via
// `auction-transitions/removal.ts`; and the transition-CHECK service that
// greys the dropdown row. The check and the write sharing this function is why
// a disabled row and a 400 can never disagree.
//
// This comment used to claim a client consumer (`ReadyConfirmationModal`). That
// component is gone — the client learns its blockers from the check API's
// `readyBlockers` field, not by calling this. The purity requirement is
// unchanged and does not depend on that claim: rule #21 / ADR 010 — Isomorphic
// Services bans I/O and non-determinism outright, which is why `now` is a
// parameter (BL-1559) rather than a `Date.now()` read inside.
//
// Pure function: no I/O, no auth, no clock of its own, same input → same output.

import {
  describeRingClosesBeforeOpensBlocker,
  findRingsClosingBeforeOpens,
} from './ring-close-schedule';

/** One unmet go-live requirement, keyed for ADR-016-style fieldErrors. */
export interface AuctionReadyBlocker {
  /** The auction field the requirement is about (fieldErrors key). */
  field: 'startDate' | 'firstLotCloseAt' | 'termsAndConditions';
  /** Operator-actionable message naming the fix. */
  message: string;
}

/** The minimum auction shape the gate needs — server rows and client form
 *  state both satisfy it (missing client-form values arrive as '' or null). */
export interface AuctionReadyGateInput {
  startDate?: string | null;
  firstLotCloseAt?: string | null;
  /**
   * BL-1312: the auction's attached terms VERSION pointer
   * (`auctions.current_terms_version_id`), not the wording.
   *
   * Terms became optional at auction creation (amended 2026-08-23), which makes
   * this gate the SOLE thing standing between a terms-less auction and a live
   * one — so it asks the pointer question directly instead of inferring it from
   * a joined string that may or may not have been selected into the caller's
   * projection.
   */
  currentTermsVersionId?: string | null;
  /**
   * BUG-1714 — every live ring's stored "First lot closes", in canonical display
   * order (index + 1 = the ring's ordinal). A ring closing at or before bidding
   * opens is the same never-opens trap the start-before-close check below
   * exists for, one level down, and the auction-level check cannot see it.
   *
   * Optional ONLY for the lot-level landing reads, which gate a lot's return
   * into bidding under a closed parent and do not project rings (declared
   * residue on BUG-1714). Every AUCTION-level door — the transition check API,
   * `lockAuctionTransitionInputsWithDeleted`, the restore lock, the status
   * cascade and open-pre-bidding — reads it through
   * `auctionRingFirstLotClosesSql`, so the dropdown row and the write agree.
   */
  ringFirstLotCloses?: ReadonlyArray<string | null> | null;
}

/**
 * Compute every unmet requirement blocking a Draft → Ready transition.
 * Reports ALL blockers at once (never fail-fast) so the dialog and the 400
 * envelope name the full fix-list in one round trip.
 *
 * `startDate` / `firstLotCloseAt` are NOT NULL columns, so their missing-value
 * branches fire only for client form state (pre-save empty inputs); the
 * server-reachable blockers are the close-after-start ordering, the BL-1559
 * past-close check, and terms.
 *
 * @param auction - The auction fields the gate reads (row or form state).
 * @param now - The clock to judge the close time against. REQUIRED, and a
 *   parameter rather than a field on {@link AuctionReadyGateInput}: this is an
 *   IsomorphicService (rule #21 / ADR 010 — Isomorphic Services), so it may not
 *   read a clock of its own, and a clock is not an auction field. Same shape as
 *   `deriveAuctionPhaseKindFromRow(row, now)`. Callers inside a transition
 *   should pass the SAME instant they derived the phase with, so the gate and
 *   the phase cannot disagree about where the auction stands.
 * @returns Empty array when the auction may go Ready; else one entry per unmet requirement.
 */
export function getAuctionReadyBlockers(
  auction: AuctionReadyGateInput,
  now: Date,
): AuctionReadyBlocker[] {
  const blockers: AuctionReadyBlocker[] = [];

  const hasStart = Boolean(auction.startDate);
  const hasClose = Boolean(auction.firstLotCloseAt);

  if (!hasStart) {
    blockers.push({
      field: 'startDate',
      message: 'A start date is required before the auction can go live.',
    });
  }
  if (!hasClose) {
    blockers.push({
      field: 'firstLotCloseAt',
      message: 'A first lot close time is required before the auction can go live.',
    });
  }
  if (hasStart && hasClose) {
    const start = new Date(auction.startDate as string);
    const close = new Date(auction.firstLotCloseAt as string);
    if (close.getTime() <= start.getTime()) {
      blockers.push({
        field: 'firstLotCloseAt',
        message: 'The first lot close must be after the start date.',
      });
    }
  }
  // BL-1559 — the schedule must still be AHEAD of the clock. The two checks
  // above are both satisfied by an auction that ran and closed last week, so
  // until this existed a stale auction passed every publishing door: its lots
  // opened for the length of BL-1299's close-time floor (one to three minutes)
  // and then flipped to Awaiting result with no explanation and no bid ever
  // possible. That floor is the right rule for ONE late lot joining a sound
  // sale and the wrong instrument for a wholly historical schedule; the rule
  // belongs here, on the auction, where the operator's fix (edit the dates) is.
  //
  // Guarded on `hasClose`, NOT folded into the `hasStart && hasClose` branch
  // above: a missing close is its own blocker, and `new Date(null)` is the
  // epoch — which is in the past, so an unguarded comparison would report two
  // blockers where the operator has one problem.
  //
  // `<=`, not `<`: a close at this exact instant is not in the future and
  // cannot open a bidding window.
  //
  // `first_lot_close_at` is `timestamptz` — an absolute instant, not a logical
  // date — so this is a plain UTC comparison. `logical-dates.md` governs
  // date-only values and does not bind here.
  //
  // Pushed AFTER the ordering check, deliberately. Both blockers key
  // `firstLotCloseAt` and can fire together, and `readyBlockersToFieldErrors`
  // is `Object.fromEntries`, so the LAST one wins the inline error. Past-close
  // must win: "reschedule the auction" is the action that resolves both, while
  // "move the close after the start" sends an operator whose whole schedule is
  // historical around the loop twice.
  if (hasClose) {
    const close = new Date(auction.firstLotCloseAt as string);
    if (close.getTime() <= now.getTime()) {
      blockers.push({
        field: 'firstLotCloseAt',
        message:
          'The first lot close time has passed — reschedule the auction before it can go live.',
      });
    }
  }
  // BUG-1714 — a ring whose first lot closes at or before bidding opens. Keyed
  // `startDate`: the missing-start blocker is the only other one on that key and
  // cannot co-occur (no start → nothing to compare against). One line PER ring,
  // because each names a different fix.
  if (hasStart) {
    for (const ring of findRingsClosingBeforeOpens({
      ringFirstLotCloses: auction.ringFirstLotCloses,
      startDate: auction.startDate,
    })) {
      blockers.push({ field: 'startDate', message: describeRingClosesBeforeOpensBlocker(ring.ordinal) });
    }
  }
  // The blocker id stays `termsAndConditions` so existing `fieldErrors`
  // consumers and their copy keep working; only the input changed.
  if (!auction.currentTermsVersionId) {
    blockers.push({
      field: 'termsAndConditions',
      message: 'Terms & conditions are required before the auction can go live.',
    });
  }

  return blockers;
}

/**
 * Map blockers to the ADR-016 `fieldErrors` envelope shape — **last message per
 * field wins**.
 *
 * That used to carry the caveat "fields are distinct in practice", which
 * BL-1559 made false: the close-after-start and past-close blockers both key
 * `firstLotCloseAt` and can fire together. The collapse is intentional and the
 * ORDER decides it — `getAuctionReadyBlockers` pushes past-close second so it
 * wins the inline error. Surfaces that must show every unmet requirement (the
 * status dropdown's disabled rows, the go-live checklist) read the blocker
 * ARRAY, not this map.
 */
export function readyBlockersToFieldErrors(
  blockers: AuctionReadyBlocker[],
): Record<string, string> {
  return Object.fromEntries(blockers.map((b) => [b.field, b.message]));
}
